import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Settings, Sun, Moon, Menu, X, RefreshCw, ChevronDown, Search } from 'lucide-react';
import { DASHBOARD_ITEM, NAV_GROUPS, visibleNavItems } from './navConfig';
import CommandPalette, { openPalette } from './CommandPalette';
import ErrorBoundary from './ErrorBoundary';

// ── New-version detection ─────────────────────────────────────────────────────
// A long-lived engine tab keeps running an old bundle after a deploy (no page
// reload ever happens in a SPA), so new features silently don't appear. Compare
// the bundle name in a freshly-fetched app shell against the running one (on
// mount, every 10 min, on tab focus) and show a tap-to-refresh banner. Never
// auto-reload here — engine pages can hold half-filled forms.
function useUpdateAvailable() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  useEffect(() => {
    const running = ([...document.scripts].map(x => x.src).find(src => /\/static\/js\/main\.[^/]*\.js/.test(src)) || '').match(/main\.[^/]*\.js/)?.[0];
    if (!running) return;
    let cancelled = false;
    const check = async () => {
      try {
        const res = await fetch(window.location.pathname, { cache: 'no-store', headers: { Accept: 'text/html' } });
        const html = await res.text();
        const latest = html.match(/main\.[^"'/]*\.js/)?.[0];
        if (!cancelled && latest && latest !== running) setUpdateAvailable(true);
      } catch {}
    };
    check();
    const t = setInterval(check, 10 * 60 * 1000);
    const onVis = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { cancelled = true; clearInterval(t); document.removeEventListener('visibilitychange', onVis); };
  }, []);
  return updateAvailable;
}

export default function Layout() {
  const { xeroConnected, theme, toggleTheme, pendingOnboarding, inspectionAlertCount, pendingLeaveCount, refreshPendingLeave, mailStopperAlertCount, customerCaseAlertCount, customerCaseRespondedCount, customerCaseFaultCount, applicantInfoAlertCount, onboardingTaskCount, onboardingInProgress, onboardingNames, sorterOfferAlertCount, currentUser } = useApp();
  const isViewer = currentUser?.role === 'viewer';
  const isAdmin = currentUser?.role === 'admin';
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const updateAvailable = useUpdateAvailable();

  // Which accordion group holds the current route (so it can auto-expand).
  const activeGroupLabel = useMemo(() => {
    const match = NAV_GROUPS.find(g =>
      g.items.some(it => location.pathname === it.to || location.pathname.startsWith(it.to + '/'))
    );
    return match?.label;
  }, [location.pathname]);

  // Open the active group on load; keep whatever else the user has toggled open.
  const [openGroups, setOpenGroups] = useState(() => (activeGroupLabel ? { [activeGroupLabel]: true } : {}));
  useEffect(() => {
    if (activeGroupLabel) {
      setOpenGroups(prev => (prev[activeGroupLabel] ? prev : { ...prev, [activeGroupLabel]: true }));
    }
  }, [activeGroupLabel]);

  // Close menu whenever route changes
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // Leave approvals happen inside the /roster iframe, which this shell can't
  // observe. Re-count on the way out so the badge clears as soon as you leave
  // the page instead of lingering until the next 3-minute poll.
  const prevPath = useRef(location.pathname);
  useEffect(() => {
    if (prevPath.current === '/roster' && location.pathname !== '/roster') refreshPendingLeave();
    prevPath.current = location.pathname;
  }, [location.pathname, refreshPendingLeave]);

  // Prevent body scroll when menu open on mobile
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  // The red count that belongs on a given link (0 = none). A team leader is
  // locked to their own team inside the calendar, so the all-team leave count
  // would mislead them — never show it to a viewer.
  const badgeFor = (to) => {
    if (to === '/dashboard' || to === '/personnel') {
      const n = (pendingOnboarding?.length || 0) + (applicantInfoAlertCount || 0);
      if (n > 0) return n;
    }
    if (to === '/roster' && !isViewer && pendingLeaveCount > 0) return pendingLeaveCount;
    if (to === '/vehicle-service' && inspectionAlertCount > 0) return inspectionAlertCount;
    if (to === '/mail-stoppers' && mailStopperAlertCount > 0) return mailStopperAlertCount;
    // A sorter waiting on a "can't work" answer — red, because the night is short until it is answered.
    if (to === '/sorters-admin' && sorterOfferAlertCount > 0) return sorterOfferAlertCount;
    if (to === '/customer-cases' && customerCaseAlertCount > 0) return customerCaseAlertCount;
    // A task nobody is told about is a task nobody does: red until it is accepted.
    if (to === '/onboarding' && onboardingTaskCount > 0) return onboardingTaskCount;
    return 0;
  };
  // Amber count (a softer "look at it" signal — distinct from the red "needs triage" count).
  // Currently only Customer Cases: a driver has replied, or a resolved case is still waiting
  // on its At fault / Not at fault call.
  const amberFor = (to) => {
    if (to === '/customer-cases') return (customerCaseRespondedCount || 0) + (customerCaseFaultCount || 0);
    // Onboarding in progress — a reminder that someone is mid-way, not a summons.
    if (to === '/onboarding') return onboardingInProgress || 0;
    return 0;
  };
  const amberTitle = (to) => {
    if (to === '/onboarding') {
      return `${onboardingInProgress} onboarding${onboardingInProgress > 1 ? 's' : ''} you have accepted and not finished`;
    }
    if (to !== '/customer-cases') return undefined;
    const bits = [];
    if (customerCaseRespondedCount > 0) bits.push(`${customerCaseRespondedCount} case${customerCaseRespondedCount > 1 ? 's' : ''} a driver has replied to — review the response`);
    if (customerCaseFaultCount > 0) bits.push(`${customerCaseFaultCount} resolved case${customerCaseFaultCount > 1 ? 's' : ''} still needing the At fault / Not at fault call`);
    return bits.join(' · ');
  };
  const badgeTitle = (to) => {
    if ((to === '/personnel' || to === '/dashboard') && applicantInfoAlertCount > 0) return `${applicantInfoAlertCount} applicant${applicantInfoAlertCount > 1 ? 's' : ''} returned requested details — review in Personnel → Applicants`;
    if (to === '/onboarding') {
      const who = onboardingNames && onboardingNames.length ? ` — ${onboardingNames.join(', ')}` : '';
      return `${onboardingTaskCount} onboarding task${onboardingTaskCount > 1 ? 's' : ''} waiting for you to accept${who}`;
    }
    if (to === '/roster') return `${pendingLeaveCount} leave request${pendingLeaveCount > 1 ? 's' : ''} waiting for a decision`;
    if (to === '/vehicle-service') return `${inspectionAlertCount} van check issue${inspectionAlertCount > 1 ? 's' : ''} need attention`;
    if (to === '/sorters-admin') return `${sorterOfferAlertCount} night sorter${sorterOfferAlertCount > 1 ? 's' : ''} asking to be let off a shift — accept a replacement or decline`;
    if (to === '/mail-stoppers') return `${mailStopperAlertCount} mail stopper${mailStopperAlertCount > 1 ? 's' : ''} need${mailStopperAlertCount > 1 ? '' : 's'} you: a new request to confirm, or a parcel was reported received`;
    if (to === '/customer-cases') return `${customerCaseAlertCount} customer case${customerCaseAlertCount > 1 ? 's' : ''} awaiting triage — confirm the driver to release`;
    return undefined;
  };

  const Badge = ({ n, title, style }) => (
    <span title={title} style={{ marginLeft:'auto', minWidth:18, height:18, borderRadius:9, background:'#ef4444', color:'#fff', fontSize:10, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', padding:'0 5px', ...style }}>
      {n}
    </span>
  );

  const renderLink = ({ to, icon: Icon, label, newTab }) => (
    // A newTab entry points at a standalone portal that renders OUTSIDE this Layout, so
    // following it in place would swallow the sidebar and leave Back as the only way out.
    // rel=noopener because target=_blank otherwise hands the opened page window.opener.
    newTab ? (
      <a key={to} href={to} target="_blank" rel="noopener noreferrer" className="nav-link">
        <Icon /> {label}
      </a>
    ) : (
    // Applicants waiting to be reviewed: Personnel opens on its Applicants tab, where the dot is.
    <NavLink key={to} to={to === '/personnel' && applicantInfoAlertCount > 0 ? '/personnel?tab=applicants' : to} className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
      <Icon /> {label}
      {(badgeFor(to) > 0 || amberFor(to) > 0) && (
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
          {amberFor(to) > 0 && <Badge n={amberFor(to)} title={amberTitle(to)} style={{ marginLeft: 0, background: '#f59e0b' }} />}
          {badgeFor(to) > 0 && <Badge n={badgeFor(to)} title={badgeTitle(to)} style={{ marginLeft: 0 }} />}
        </span>
      )}
    </NavLink>
    )
  );

  const visibleItems = (items) => visibleNavItems(items, { isViewer, isAdmin, login: currentUser?.login });

  const NavGroup = ({ group }) => {
    const items = visibleItems(group.items);
    if (items.length === 0) return null;
    const open = !!openGroups[group.label];
    // When collapsed, surface any pending badges from the hidden children so an
    // alert isn't buried inside a closed group.
    const groupBadge = items.reduce((sum, it) => sum + badgeFor(it.to), 0);
    const groupAmber = items.reduce((sum, it) => sum + amberFor(it.to), 0);
    const Icon = group.icon;
    return (
      <div className="nav-group">
        <button
          type="button"
          className={`nav-group-header${open ? ' open' : ''}`}
          onClick={() => setOpenGroups(prev => ({ ...prev, [group.label]: !prev[group.label] }))}
          aria-expanded={open}
        >
          <Icon /> <span className="nav-group-label">{group.label}</span>
          {!open && (groupBadge > 0 || groupAmber > 0) && (
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
              {groupAmber > 0 && <Badge n={groupAmber} style={{ marginLeft: 0, background: '#f59e0b' }} />}
              {groupBadge > 0 && <Badge n={groupBadge} style={{ marginLeft: 0 }} />}
            </span>
          )}
          <ChevronDown className="nav-group-chevron" size={14} style={{ marginLeft: (!open && (groupBadge > 0 || groupAmber > 0)) ? 6 : 'auto' }} />
        </button>
        {open && <div className="nav-group-items">{items.map(renderLink)}</div>}
      </div>
    );
  };

  const SidebarContent = () => (
    <>
      <div className="sidebar-brand">
        <div className="brand-logo">HOLLYPAUL</div>
        <div className="brand-sub">App Engine v1.0</div>
      </div>
      <button type="button" className="palette-trigger" onClick={openPalette}>
        <Search size={13} /> <span>Search…</span>
        <kbd className="palette-kbd">⌘K</kbd>
      </button>
      <nav className="sidebar-nav">
        {renderLink(DASHBOARD_ITEM)}
        {NAV_GROUPS.map(group => <NavGroup key={group.label} group={group} />)}
        {!isViewer && (
          <>
            <div className="nav-section-label">System</div>
            <NavLink to="/settings" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
              <Settings /> Settings
            </NavLink>
          </>
        )}
      </nav>
      <div className="sidebar-footer">
        {isViewer && currentUser?.name && (
          <div style={{ marginBottom: 10, padding: '8px 10px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', fontSize: '0.75rem' }}>
            <div style={{ color: 'var(--hp-dim)', letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: '0.65rem', marginBottom: 3 }}>Logged in as</div>
            <div style={{ color: 'var(--hp-text)', fontWeight: 700 }}>{currentUser.name}</div>
            {currentUser.team && <div style={{ color: 'var(--hp-gold)', fontSize: '0.7rem', marginTop: 2 }}>{currentUser.team} Team</div>}
          </div>
        )}
        {!isViewer && (
          <div className="xero-badge" style={{ marginBottom: 10 }}>
            <div className={`xero-dot${xeroConnected ? '' : ' disconnected'}`} />
            <span>Xero {xeroConnected ? 'Connected' : 'Not connected'}</span>
          </div>
        )}
        <button
          onClick={toggleTheme}
          style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'8px 12px', background:'var(--hp-surface)', border:'1px solid var(--hp-border)', borderRadius:'var(--radius)', color:'var(--hp-dim)', cursor:'pointer', fontSize:'0.75rem', fontFamily:'var(--font-body)', fontWeight:500 }}
        >
          {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
          {theme === 'dark' ? 'Light mode' : 'Dark mode'}
        </button>
      </div>
    </>
  );

  return (
    <div className="app-shell">
      {/* ── Desktop sidebar ── */}
      <aside className="sidebar sidebar-desktop">
        <SidebarContent />
      </aside>

      {/* ── Mobile: top bar + drawer ── */}
      <div className="mobile-topbar">
        <button onClick={() => setMenuOpen(true)} className="mobile-menu-btn" aria-label="Open menu">
          <Menu size={22} />
        </button>
        <span className="mobile-brand">HOLLYPAUL</span>
        <button onClick={openPalette} className="mobile-theme-btn" aria-label="Search" style={{ marginRight: 4 }}>
          <Search size={18} />
        </button>
        <button onClick={toggleTheme} className="mobile-theme-btn" aria-label="Toggle theme">
          {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
        </button>
      </div>

      {/* Mobile drawer overlay */}
      {menuOpen && (
        <div className="mobile-overlay" onClick={() => setMenuOpen(false)} />
      )}

      {/* Mobile drawer */}
      <aside className={`sidebar sidebar-mobile${menuOpen ? ' open' : ''}`}>
        <button onClick={() => setMenuOpen(false)} className="mobile-close-btn" aria-label="Close menu">
          <X size={20} />
        </button>
        <SidebarContent />
      </aside>

      <main className="main-content">
        {updateAvailable && (
          <button
            onClick={() => window.location.reload()}
            style={{ width: '100%', padding: '12px 16px', border: 'none', background: 'var(--hp-gold)', color: 'var(--hp-black)', fontWeight: 800, fontSize: '0.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, borderRadius: 'var(--radius)', marginBottom: 16 }}
          >
            <RefreshCw size={15} /> App update available — tap to refresh
          </button>
        )}
        {/* Keyed on the path so navigating to another page clears a crash — the sidebar
            is still there, so the user is never stuck on a dead screen. */}
        <ErrorBoundary key={location.pathname} label={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </main>

      <CommandPalette />
    </div>
  );
}
